/**
 * @fileoverview Editor-only preview context for the ArticleIndex block (C-24, owner-approved).
 *
 * The Studio editor preview has no ArticleRenderContext (only the published web-shell engine provides it), so an
 * ArticleIndex would render nothing there. The editor wraps its preview in this provider instead: SectionRenderer then
 * renders the visible placeholder, and the folder / topic NAMES (known to the editor, not to the document, which only
 * holds ids) let the placeholder note say what the list will contain. Published pages and funnels never have a
 * provider → `null` → today's output, byte-identical (`npm run ssr-gate`).
 */
import React from 'react';
import type { ArticleRenderContextValue } from '@modules/screen-view-v2/context/ArticleRenderContext';

export interface ArticleIndexPreviewValue {
  /** Stand-in article context (no runtime → the placeholder branch of ArticleIndex). */
  article: ArticleRenderContextValue;
  folderNames: Readonly<Record<string, string>>;
  topicLabels: Readonly<Record<string, string>>;
}

export const ArticleIndexPreviewContext = React.createContext<ArticleIndexPreviewValue | null>(null);

ArticleIndexPreviewContext.displayName = 'ArticleIndexPreviewContext';

export function createArticleIndexPreview(
  defaultLanguage: string,
  folders: ReadonlyArray<{ id: string; name: string }>,
  topics: ReadonlyArray<{ id: string; label: string }>,
): ArticleIndexPreviewValue {
  return {
    article: { presentation: null, runtime: null, outline: null, defaultLanguage },
    folderNames: Object.fromEntries(folders.map((folder) => [folder.id, folder.name])),
    topicLabels: Object.fromEntries(topics.map((topic) => [topic.id, topic.label])),
  };
}
